import React from 'react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { FileText, Search, Clock, CheckCircle, ShieldCheck, AlertCircle } from 'lucide-react';

const milestones = [
  {
    icon: FileText,
    title: 'Application Filed',
    time: 'Day 1',
    description: 'We file your trademark application with the USPTO after completing a thorough search and review.',
    color: 'bg-[#7C3AED]',
    ring: 'ring-[#7C3AED]/20',
  },
  {
    icon: Search,
    title: 'USPTO Examination',
    time: 'Month 3-4',
    description: 'A USPTO examining attorney reviews your application for compliance with federal trademark law.',
    color: 'bg-[#14B8A6]',
    ring: 'ring-[#14B8A6]/20',
  },
  {
    icon: AlertCircle,
    title: 'Office Action (if any)',
    time: 'Month 4-6',
    description: 'If issues arise, we respond to office actions with legal arguments to keep your application on track.',
    color: 'bg-amber-500',
    ring: 'ring-amber-500/20',
  },
  {
    icon: Clock,
    title: 'Publication Period',
    time: 'Month 6-8',
    description: 'Your mark is published in the Official Gazette for a 30-day opposition window where third parties can object.',
    color: 'bg-[#7C3AED]',
    ring: 'ring-[#7C3AED]/20',
  },
  {
    icon: CheckCircle,
    title: 'Notice of Allowance',
    time: 'Month 8-10',
    description: 'If no opposition is filed, you receive a Notice of Allowance or proceed directly to registration.',
    color: 'bg-[#14B8A6]',
    ring: 'ring-[#14B8A6]/20',
  },
  {
    icon: ShieldCheck,
    title: 'Registration Certificate',
    time: 'Month 10-12',
    description: 'Your federal trademark registration certificate is issued, granting nationwide brand protection.',
    color: 'bg-emerald-500',
    ring: 'ring-emerald-500/20',
  },
];

const TimelineStep = ({ milestone, index, isLast }) => {
  const step = useScrollAnimation({ threshold: 0.15 });
  const Icon = milestone.icon;

  return (
    <div ref={step.ref} className={`relative scroll-fade-up scroll-delay-${Math.min(index + 1, 6)} ${step.isVisible ? 'scroll-visible' : ''}`}>
      <div className="flex gap-5">
        <div className="flex flex-col items-center">
          <div className={`w-12 h-12 rounded-full ${milestone.color} ring-4 ${milestone.ring} flex items-center justify-center flex-shrink-0 z-10`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
          {!isLast && <div className="w-0.5 flex-1 bg-gradient-to-b from-zinc-200 to-zinc-100 mt-2" />}
        </div>

        <div className="pb-10">
          <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400 block mb-1">
            {milestone.time}
          </span>
          <h3 className="text-lg font-bold text-zinc-900 mb-1.5" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            {milestone.title}
          </h3>
          <p className="text-sm text-zinc-500 leading-relaxed max-w-md">
            {milestone.description}
          </p>
        </div>
      </div>
    </div>
  );
};

const RegistrationTimeline = () => {
  const header = useScrollAnimation({ threshold: 0.15 });

  return (
    <section data-testid="timeline-section" className="py-24 md:py-32 bg-zinc-50 relative overflow-hidden">
      <div className="absolute top-20 right-0 w-[300px] h-[300px] rounded-full bg-[#14B8A6]/5 blur-[100px]" />

      <div className="mx-auto w-full max-w-7xl px-4 md:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          <div ref={header.ref} className={`lg:sticky lg:top-32 scroll-fade-up ${header.isVisible ? 'scroll-visible' : ''}`}>
            <span className="inline-block bg-[#14B8A6]/10 text-[#14B8A6] px-4 py-1.5 rounded-full text-xs font-bold tracking-wide mb-4">
              REGISTRATION TIMELINE
            </span>
            <h2 className="text-4xl sm:text-5xl font-black tracking-tight text-zinc-900 mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              From Filing to Federal Protection
            </h2>
            <p className="text-base text-zinc-500 leading-relaxed mb-6">
              The full trademark registration process typically takes 8-12 months. Here is what to expect at each stage of your USPTO application journey.
            </p>

            <div className="bg-white rounded-2xl border border-zinc-100 p-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-[#7C3AED]/5 rounded-xl">
                  <div className="text-3xl font-black text-[#7C3AED]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>8-12</div>
                  <div className="text-xs text-zinc-500 mt-1">Months Total</div>
                </div>
                <div className="text-center p-4 bg-[#14B8A6]/5 rounded-xl">
                  <div className="text-3xl font-black text-[#14B8A6]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>6</div>
                  <div className="text-xs text-zinc-500 mt-1">Key Stages</div>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-0">
            {milestones.map((m, i) => (
              <TimelineStep key={i} milestone={m} index={i} isLast={i === milestones.length - 1} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default RegistrationTimeline;
